import React, { useEffect, useState } from 'react';
import LeadDetailPanel from '../components/leads/LeadDetailPanel';
import { MagnifyingGlassIcon, FunnelIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { ChannelIcon } from '../components/settings/channelIcons';

interface Lead {
  id: string;
  external_id: string;
  source_platform: string;
  source_url: string | null;
  original_content: string;
  original_language?: string | null;
  english_translation?: string | null;
  detected_at: string;
  classification: string;
  confidence_score: number;
  status: string;
  name: string | null;
  contact_info: any;
  intent_type: string | null;
  stay_duration: string | null;
  target_location: any;
  property_preferences: any;
  budget_range: any;
  timeline: string | null;
  special_requirements: string[] | null;
  enrichment_status?: string | null;
  enrichment_stage?: string | null;
  enriched_at?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

const ENRICHING = (s?: string | null) => s === 'pending' || s === 'running';

const ENRICH_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: '⏳ queued', cls: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200' },
  running: { label: '⏳ enriching', cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 animate-pulse' },
  done: { label: '✓ enriched', cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  failed: { label: '! enrich failed', cls: 'bg-red-50 text-red-600 ring-1 ring-red-200' },
};

const CLASSIFICATION_STYLES: Record<string, string> = {
  qualified: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  potential: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  irrelevant: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200',
  disqualified: 'bg-red-50 text-red-600 ring-1 ring-red-200',
};

const STATUS_STYLES: Record<string, string> = {
  detected: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  contacted: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  engaged: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
  converted: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  rejected: 'bg-red-50 text-red-600 ring-1 ring-red-200',
};

function timeAgo(dateString: string) {
  const diff = Date.now() - new Date(dateString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const LeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [classFilter, setClassFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [counts, setCounts] = useState({ total: 0, qualified: 0, potential: 0, disqualified: 0 });

  useEffect(() => { fetchLeads(); }, [classFilter, platformFilter, statusFilter, sortBy, sortOrder, pagination.page]);
  useEffect(() => { fetchCounts(); }, []);

  // Merge a fresh lead (e.g. after enrichment) into the list + open panel,
  // without any reload.
  const applyLeadUpdate = (updated: Lead) => {
    setLeads(prev => prev.map(l => (l.id === updated.id ? { ...l, ...updated } : l)));
    setSelectedLead(prev => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  // While the open lead is enriching, poll it so the progress bar + result
  // update live. Switching leads just re-points the poll; the server keeps
  // enriching every lead in the background regardless of what's on screen.
  useEffect(() => {
    if (!selectedLead || !ENRICHING(selectedLead.enrichment_status)) return;
    const id = selectedLead.id;
    const t = setInterval(() => {
      fetch(`/api/v1/leads/${id}`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d && d.id) applyLeadUpdate(d); })
        .catch(() => {});
    }, 2500);
    return () => clearInterval(t);
  }, [selectedLead?.id, selectedLead?.enrichment_status]);

  const fetchLeads = () => {
    setLoading(true);
    const p = new URLSearchParams({
      page: String(pagination.page), limit: String(pagination.limit),
      sort_by: sortBy, sort_order: sortOrder,
    });
    if (classFilter) p.append('classification', classFilter);
    if (platformFilter) p.append('platform', platformFilter);
    if (statusFilter) p.append('status', statusFilter);
    fetch(`/api/v1/leads?${p}`)
      .then(r => r.json())
      .then(d => {
        setLeads(d.data || []);
        if (d.pagination) setPagination(prev => ({ ...prev, total: d.pagination.total, pages: d.pagination.pages }));
      })
      .finally(() => setLoading(false));
  };

  const fetchCounts = () => {
    fetch('/api/v1/leads/stats').then(r => r.json()).then(d => {
      setCounts({
        total: d.total_leads || 0,
        qualified: d.by_classification?.qualified || 0,
        potential: d.by_classification?.potential || 0,
        disqualified: d.by_classification?.disqualified || 0,
      });
    });
  };

  const toggleSort = (field: string) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
    setPagination(p => ({ ...p, page: 1 }));
  };

  const filterBtns = [
    { label: 'All', value: '', count: counts.total },
    { label: 'Qualified', value: 'qualified', count: counts.qualified },
    { label: 'Potential', value: 'potential', count: counts.potential },
    { label: 'Disqualified', value: 'disqualified', count: counts.disqualified },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {loading ? 'Loading…' : `${pagination.total} leads detected`}
          </p>
        </div>
      </div>

      {/* Classification tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {filterBtns.map(b => (
          <button
            key={b.value}
            onClick={() => { setClassFilter(b.value); setPagination(p => ({ ...p, page: 1 })); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              classFilter === b.value
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {b.label} <span className="ml-1 opacity-70">{b.count}</span>
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-4 w-4 text-slate-400" />
          <span className="text-sm text-slate-500 font-medium">Filter:</span>
        </div>
        <select
          value={platformFilter}
          onChange={e => { setPlatformFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Platforms</option>
          <option value="reddit">Reddit</option>
          <option value="telegram">Telegram</option>
          <option value="facebook">Facebook</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Status</option>
          <option value="detected">Detected</option>
          <option value="contacted">Contacted</option>
          <option value="engaged">Engaged</option>
          <option value="converted">Converted</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20">
            <MagnifyingGlassIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No leads found</p>
            <p className="text-slate-400 text-sm mt-1">
              {classFilter || platformFilter ? 'Try adjusting filters' : 'Agent will populate leads once scanning starts'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-1/3">
                    Lead
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                    onClick={() => toggleSort('detected_at')}
                  >
                    <span className="flex items-center gap-1">
                      Detected <ChevronUpDownIcon className="h-3.5 w-3.5" />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Classification
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                    onClick={() => toggleSort('confidence_score')}
                  >
                    <span className="flex items-center gap-1">
                      Score <ChevronUpDownIcon className="h-3.5 w-3.5" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map(lead => (
                  <tr
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-slate-800 text-sm flex items-center gap-2">
                        {lead.name || lead.external_id?.slice(0, 20) || 'Unknown'}
                        {lead.classification === 'qualified' && lead.enrichment_status && ENRICH_BADGE[lead.enrichment_status] && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${ENRICH_BADGE[lead.enrichment_status].cls}`}>
                            {ENRICH_BADGE[lead.enrichment_status].label}
                          </span>
                        )}
                      </div>
                      <div className="text-slate-400 text-xs mt-0.5 truncate max-w-xs">
                        {lead.original_content?.slice(0, 80)}…
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center gap-1.5 text-sm capitalize text-slate-600">
                        <ChannelIcon id={lead.source_platform} className="w-4 h-4" /> {lead.source_platform}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="text-sm text-slate-600">{timeAgo(lead.detected_at)}</span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-600">
                      {lead.target_location?.city || '—'}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${CLASSIFICATION_STYLES[lead.classification] || CLASSIFICATION_STYLES.irrelevant}`}>
                        {lead.classification}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[lead.status] || STATUS_STYLES.detected}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5 w-16">
                          <div
                            className={`h-1.5 rounded-full ${lead.confidence_score >= 75 ? 'bg-emerald-500' : lead.confidence_score >= 40 ? 'bg-amber-400' : 'bg-slate-300'}`}
                            style={{ width: `${Math.min(100, lead.confidence_score)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 tabular-nums">{lead.confidence_score.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>Page {pagination.page} of {pagination.pages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
              disabled={pagination.page === 1}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <span>{pagination.limit} per page</span>
        </div>
      )}

      <LeadDetailPanel lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdate={applyLeadUpdate} />
    </div>
  );
};

export default LeadsPage;
